(() => {
  const selector = "[data-sonic-home-hero-video]";
  const controllers = new WeakMap();

  function init(media) {
    if (!media || media.dataset.videoInitialized === "true") return;
    media.dataset.videoInitialized = "true";

    const video = media.querySelector(".sonic-home-hero__video");
    if (!video || window.matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData) return;

    const poster = media.querySelector(".sonic-home-hero__poster img");
    const hero = media.closest('[data-sonic-home-hero-motion]');
    const mobile = window.matchMedia('(max-width: 899px)');
    const introReady = () => !mobile.matches || !hero?.dataset.sonicEntrance || ['complete', 'static'].includes(hero.dataset.sonicEntrance);
    let active = false;
    let loaded = false;
    let posterReady = !poster;
    let disposed = false;
    let scheduled = false;
    let idleId = null;
    let timerId = null;
    let rafId = null;

    const play = () => {
      if (disposed || !active || !loaded || document.hidden) return;
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    };

    const load = () => {
      scheduled = false;
      if (disposed || loaded || !active || !introReady() || document.hidden) return;
      loaded = true;
      entranceObserver?.disconnect();

      let sources = Array.from(video.querySelectorAll("source[data-src]"));
      const matching = sources.filter(source => source.media && window.matchMedia(source.media).matches);
      if (matching.length) {
        sources.forEach(source => { if (!matching.includes(source)) source.remove(); });
        sources = matching;
      }
      const mp4 = sources.filter(source => source.type === "video/mp4" && Number(source.dataset.videoHeight) > 0);
      if (mp4.length) {
        const maxHeight = window.matchMedia("(max-width: 899px)").matches ? 720 : 1080;
        const ordered = mp4.sort((a, b) => Number(a.dataset.videoHeight) - Number(b.dataset.videoHeight));
        const selected = ordered.filter(source => Number(source.dataset.videoHeight) <= maxHeight).pop() || ordered[0];
        sources.forEach(source => { if (source !== selected) source.remove(); });
        sources = [selected];
      }
      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute("data-src");
      });

      video.load();
      play();
    };

    const scheduleLoad = () => {
      if (disposed || loaded || scheduled || !active || !posterReady || !introReady() || document.hidden) return;
      scheduled = true;
      // Let the critical poster paint before the video competes for bandwidth.
      rafId = window.requestAnimationFrame(() => {
        rafId = window.requestAnimationFrame(() => {
          if ("requestIdleCallback" in window) {
            idleId = window.requestIdleCallback(load, { timeout: 250 });
          } else {
            timerId = window.setTimeout(load, 0);
          }
        });
      });
    };

    // Mobile video starts after the shared entrance has presented the critical content.
    const entranceObserver = hero ? new MutationObserver(scheduleLoad) : null;
    entranceObserver?.observe(hero, { attributes: true, attributeFilter: ['data-sonic-entrance'] });
    mobile.addEventListener('change', scheduleLoad);

    const onPlaying = () => media.classList.add("is-video-ready");
    const onError = () => media.classList.remove("is-video-ready");
    video.addEventListener("playing", onPlaying);
    video.addEventListener("error", onError);

    const observer = new IntersectionObserver(
      ([entry]) => {
        active = Boolean(entry?.isIntersecting);
        if (active) {
          scheduleLoad();
          play();
        } else {
          video.pause();
        }
      },
      { threshold: 0.05 },
    );

    observer.observe(media);

    const onPosterReady = async () => {
      if (poster?.naturalWidth && poster.decode) {
        try { await poster.decode(); } catch (_) { /* A failed poster must not block playback. */ }
      }
      posterReady = true;
      scheduleLoad();
    };

    if (!poster || poster.complete) {
      onPosterReady();
    } else {
      poster.addEventListener("load", onPosterReady, { once: true });
      poster.addEventListener("error", onPosterReady, { once: true });
    }

    const onVisibility = () => {
      if (document.hidden) video.pause();
      else { scheduleLoad(); play(); }
    };
    document.addEventListener("visibilitychange", onVisibility);
    controllers.set(media, () => {
      disposed = true;
      observer.disconnect();
      entranceObserver?.disconnect();
      mobile.removeEventListener('change', scheduleLoad);
      window.cancelAnimationFrame(rafId);
      if (idleId !== null) window.cancelIdleCallback(idleId);
      window.clearTimeout(timerId);
      poster?.removeEventListener("load", onPosterReady);
      poster?.removeEventListener("error", onPosterReady);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("error", onError);
      document.removeEventListener("visibilitychange", onVisibility);
      video.pause();
      controllers.delete(media);
      delete media.dataset.videoInitialized;
    });
  }

  function initAll(root = document) {
    root.querySelectorAll(selector).forEach(init);
  }

  initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll(selector).forEach((media) => controllers.get(media)?.());
  });
})();
